import { useCallback } from 'react';

import type { AxiosError } from '@mint-lib/api';
import {
  InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@mint-lib/query-client';
import { MediaPlan, Suggestions } from '@mint-sdk/api-recommendations';
import { useAtom } from 'jotai/index';

import useSuggestionVersion from '../hooks/useSuggestionVersion.js';
import { suggestionSelectedFiltersAtom } from '../store/index.js';
import { useCreateNewMediaPlanVersion } from './mediaPlan.js';

type ApiError = {
  detail: string;
};

export const useInfiniteSuggestionQuery = (
  params: Suggestions.GetFilterRequestType,
) => {
  const { addSuggestionVersion } = useSuggestionVersion();

  const suggestionParams = addSuggestionVersion(params);

  return useInfiniteQuery({
    ...Suggestions.listInfiniteQueryOptions({
      params: { ...suggestionParams, page: 1, pageSize: 10 },
    }),
    getNextPageParam: (lastPage) => {
      const nextPage =
        lastPage.pages >= lastPage.page ? lastPage.page + 1 : undefined;
      if (!nextPage) {
        return undefined;
      }

      return {
        ...suggestionParams,
        page: nextPage,
        pageSize: 10,
      };
    },
  });
};

export const useSuggestionFilterQuery = (
  params: Suggestions.GetFilterRequestType,
) => {
  const { addSuggestionVersion } = useSuggestionVersion();

  const suggestionParams = addSuggestionVersion(params);

  return useQuery({
    ...Suggestions.getFiltersQueryOptions({
      params: { ...suggestionParams },
    }),
  });
};

export const useRejectSuggestion = () => {
  return useMutation({
    mutationFn: ({ id, reason }: { id: number | string; reason: string }) =>
      Suggestions.reject({
        urlParams: { id },
        data: { reason },
      }),
  });
};

export const useAcceptSuggestion = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: createNewVersionAsync } = useCreateNewMediaPlanVersion();
  return useMutation({
    mutationFn: ({
      suggestionId,
      mediaPlanId,
    }: {
      suggestionId: number | string;
      mediaPlanId?: number;
    }) =>
      Suggestions.accept({
        urlParams: { suggestionId: suggestionId },
        data: { suggestionId, mediaPlanId },
      }),

    onMutate: async (variables) => {
      if (variables.mediaPlanId) {
        const versions = await queryClient.fetchQuery({
          ...MediaPlan.getVersionsQueryOptions({
            params: { mediaPlanId: variables.mediaPlanId },
          }),
        });
        const data = await createNewVersionAsync(versions[0].id);
        return data;
      }
    },
  });
};
export const usePublishSuggestion = () => {
  const { addSuggestionVersion } = useSuggestionVersion();
  const { mutate, isPending } = Suggestions.bulkPublish();

  const publishSuggestion = useCallback(
    (
      suggestion: Pick<Suggestions.SuggestionResponseType, 'id' | 'journeyId'>,
      options?: Parameters<typeof mutate>[1],
    ) =>
      mutate(
        {
          data: addSuggestionVersion({
            recommendationIds: [suggestion.id],
            campaignIds: [suggestion.journeyId],
          }),
        },
        options,
      ),
    [addSuggestionVersion, mutate],
  );

  return { mutate: publishSuggestion, isPending };
};

export const useReadSuggestion = (
  selectedSuggestionFilter: Suggestions.GetFilterRequestType,
) => {
  const queryClient = useQueryClient();
  const { updateSuggestionList } = useSuggestionListOptimisticUpdate();
  return useMutation({
    mutationFn: (suggestion: Suggestions.SuggestionResponseType) =>
      Suggestions.read({ urlParams: { id: suggestion.id } }),
    onMutate: async (suggestion: Suggestions.SuggestionResponseType) => {
      await queryClient.cancelQueries({
        queryKey: Suggestions.listInfiniteQueryOptions({
          params: { ...selectedSuggestionFilter, page: 1, pageSize: 10 },
        }).queryKey,
      });

      // Snapshot the previous value
      const previousSuggestions = queryClient.getQueryData(
        Suggestions.listInfiniteQueryOptions({
          params: { ...selectedSuggestionFilter, page: 1, pageSize: 10 },
        }).queryKey,
      );
      // Optimistically update to the new value
      updateSuggestionList(
        { ...suggestion, notRead: false },
        selectedSuggestionFilter,
      );

      return { previousSuggestions };
    },
  });
};

// Update suggestion list after success accept, reject, publish etc
export const useSuggestionListOptimisticUpdate = () => {
  const queryClient = useQueryClient();

  const updateSuggestionItems = (
    items: Suggestions.SuggestionResponseType[],
    data: Suggestions.SuggestionResponseType,
  ): Suggestions.SuggestionResponseType[] =>
    items.map((item) => (item.id === data.id ? data : item));

  const updateSuggestionList = (
    data: Suggestions.SuggestionResponseType,
    selectedSuggestionFilter: Suggestions.GetFilterRequestType,
  ) => {
    queryClient.setQueriesData<InfiniteData<Suggestions.ListResponseType>>(
      {
        queryKey: Suggestions.listInfiniteQueryOptions({
          params: { ...selectedSuggestionFilter, page: 1, pageSize: 10 },
        }).queryKey,
      },
      (previous) => {
        if (!previous) {
          return undefined;
        }
        const { pageParams, pages } = previous;
        const updatedPages = pages.map((el) => ({
          ...el,
          items: updateSuggestionItems(el.items, data),
        }));
        return { pageParams, pages: updatedPages };
      },
    );
  };
  return { updateSuggestionList };
};

export const useRollbackSuggestion = () => {
  const queryClient = useQueryClient();
  const [selectedSuggestionFilter, setSuggestionFilter] = useAtom(
    suggestionSelectedFiltersAtom,
  );

  const queryKey = Suggestions.listInfiniteQueryOptions({
    params: { ...selectedSuggestionFilter, page: 1, pageSize: 10 },
  }).queryKey;

  return useMutation<
    Suggestions.SuggestionResponseType,
    AxiosError<ApiError>,
    number
  >({
    mutationFn: (id: number) => {
      return Suggestions.rollback({
        urlParams: { id },
      });
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey, exact: true });
    },
  });
};

export const useGenerateSuggestion = () => {
  const queryClient = useQueryClient();
  const [selectedSuggestionFilter, setSuggestionFilter] = useAtom(
    suggestionSelectedFiltersAtom,
  );

  const queryKey = Suggestions.listInfiniteQueryOptions({
    params: { ...selectedSuggestionFilter, page: 1, pageSize: 10 },
  }).queryKey;

  return useMutation<
    { status: string },
    AxiosError<ApiError>,
    Suggestions.GenerateRequestType
  >({
    mutationFn: (data: Suggestions.GenerateRequestType) => {
      return Suggestions.generate({
        urlParams: { id: data.suggestionId },
        data,
      });
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey, exact: true });
    },
  });
};
